import React, { useCallback, useEffect, useState } from 'react';
import {
    Loader2,
    Download,
    Search,
    ClipboardList,
    ShieldAlert,
    MapPin,
    Tag,
    ChevronRight,
    Calendar,
    Image as ImageIcon,
    MessageSquare,
    RefreshCw,
    CircleDot,
    CircleCheck,
    Layers,
    Baby,
    Home,
    Sparkles,
    ClipboardCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Paginacion, { paginar } from './Paginacion';
import DetalleReporte, { BadgeEstado, QueSeReporto } from './DetalleReporte';
import axiosInstance from '../../utils/axiosConfig';

const API = '/api/calidad/sura-reporta';

const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const rangoInicial = () => {
    const hoy = new Date();
    return { desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth() - 5, 1)), hasta: isoLocal(hoy) };
};

const fechaHora = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};

// El backend puede mandar textos sueltos u objetos {id, label}: se normaliza aqui.
const etiqueta = (v) => (v == null ? '' : typeof v === 'string' ? v : (v.label || v.nombre || v.id || ''));
const nombrePersona = (r) => etiqueta(r.persona) || r.empleado || r.usuario || 'Sin nombre';
const urlFoto = (f) => {
    const ruta = typeof f === 'string' ? f : (f?.url || f?.ruta || '');
    if (!ruta) return null;
    return ruta.startsWith('/api') || ruta.startsWith('http') ? ruta : `/api${ruta.startsWith('/') ? '' : '/'}${ruta}`;
};

const descargarBlob = (data, nombre) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    a.click();
    window.URL.revokeObjectURL(url);
};

const selectCls = 'px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-500';

// Seguimiento tipo ticket: solo los reportes con falla tienen estado.
// Los reportes entran directo en revision.
// Tarjetas de arriba: muestran cuantos hay y filtran la lista (como en Tickets)
const PESTANAS_ESTADO = [
    { id: 'en_revision', label: 'En revisión', icon: CircleDot, color: '#0062A5', bg: '#eff6ff' },
    { id: 'cerrado', label: 'Cerrados', icon: CircleCheck, color: '#047857', bg: '#ecfdf5' },
    { id: '', label: 'Todos', icon: Layers, color: '#475569', bg: '#f1f5f9' },
];

const ICONO_LINEA = { personal: Sparkles, bebe: Baby, hogar: Home };

// `refresco`: el padre lo incrementa cuando un reporte cambio desde afuera (p.ej.
// abierto desde una notificacion), para recargar sin perder los filtros.
const CalidadReportes = ({ refresco = 0 }) => {
    const [catalogos, setCatalogos] = useState(null);
    const [filtros, setFiltros] = useState(() => ({
        ...rangoInicial(), estado: 'en_revision', linea: '', desviacion: '', region: '', solo_fallas: true, critico: false, q: '',
    }));
    const [busqueda, setBusqueda] = useState('');
    const [reportes, setReportes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [conteoEstados, setConteoEstados] = useState({});
    const [detalleId, setDetalleId] = useState(null);
    const [exportando, setExportando] = useState(false);
    const [pagina, setPagina] = useState(1);

    // Filtros nuevos: volver a la primera pagina.
    useEffect(() => { setPagina(1); }, [filtros]);

    useEffect(() => {
        axiosInstance.get(`${API}/catalogos`)
            .then(res => setCatalogos(res.data))
            .catch(() => setCatalogos({}));
    }, []);

    // La busqueda espera a que el usuario deje de escribir.
    useEffect(() => {
        const t = setTimeout(() => setFiltros(prev => (prev.q === busqueda ? prev : { ...prev, q: busqueda })), 350);
        return () => clearTimeout(t);
    }, [busqueda]);

    const params = useCallback(() => {
        const p = { desde: filtros.desde, hasta: filtros.hasta };
        if (filtros.estado) p.estado = filtros.estado;
        if (filtros.linea) p.linea = filtros.linea;
        if (filtros.desviacion) p.desviacion = filtros.desviacion;
        if (filtros.region) p.region = filtros.region;
        if (filtros.solo_fallas) p.solo_fallas = 1;
        if (filtros.critico) p.critico = 1;
        if (filtros.q.trim()) p.q = filtros.q.trim();
        return p;
    }, [filtros]);

    // `silencioso`: refresca sin mostrar la carga (p.ej. tras actuar en el detalle).
    const cargar = useCallback(async (silencioso = false) => {
        if (!silencioso) setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(`${API}/reportes`, { params: params() });
            const data = res.data;
            setReportes(Array.isArray(data) ? data : (data.reportes || data.diagnosticos || []));
            setConteoEstados({ ...(data?.conteo_estados || {}), todos: data?.conteo_todos });
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudieron cargar los reportes');
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => { cargar(); }, [cargar]);
    useEffect(() => { if (refresco) cargar(true); }, [refresco]); // eslint-disable-line react-hooks/exhaustive-deps

    const exportar = async () => {
        setExportando(true);
        try {
            const res = await axiosInstance.get(`${API}/reportes/excel`, { params: params(), responseType: 'blob' });
            descargarBlob(res.data, `Sura_Reporta_${filtros.desde}_${filtros.hasta}.xlsx`);
        } catch {
            toast.error('No se pudo exportar el Excel');
        } finally {
            setExportando(false);
        }
    };

    const set = (campo, valor) => setFiltros(prev => ({ ...prev, [campo]: valor }));

    return (
        <div className="space-y-4">
            {/* Estado del seguimiento */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                {PESTANAS_ESTADO.map(t => {
                    const activo = filtros.estado === t.id;
                    const n = conteoEstados[t.id || 'todos'];
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id || 'todos'}
                            type="button"
                            onClick={() => set('estado', t.id)}
                            className={`flex items-center gap-4 px-5 py-[1.125rem] bg-white rounded-2xl border-[1.5px] text-left transition-all ${activo ? 'border-sura-blue shadow-md' : 'border-slate-100 hover:border-slate-200 hover:shadow-sm'}`}
                        >
                            <span className="w-[46px] h-[46px] rounded-[14px] flex items-center justify-center shrink-0" style={{ background: t.bg, color: t.color }}>
                                <Icon size={22} />
                            </span>
                            <span>
                                <span className="block text-2xl font-bold text-slate-900 leading-tight">{n ?? 0}</span>
                                <span className="block text-sm text-slate-500">{t.label}</span>
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-400" />
                        <input type="date" value={filtros.desde} max={filtros.hasta} onChange={e => set('desde', e.target.value)} className={selectCls} />
                        <span className="text-gray-400 text-sm">a</span>
                        <input type="date" value={filtros.hasta} min={filtros.desde} onChange={e => set('hasta', e.target.value)} className={selectCls} />
                    </div>
                    <select value={filtros.linea} onChange={e => set('linea', e.target.value)} className={selectCls}>
                        <option value="">Todas las líneas</option>
                        {(catalogos?.lineas || []).map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                    <select value={filtros.desviacion} onChange={e => set('desviacion', e.target.value)} className={selectCls}>
                        <option value="">Todos los tipos de falla</option>
                        {(catalogos?.desviaciones || []).map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                    </select>
                    <select value={filtros.region} onChange={e => set('region', e.target.value)} className={selectCls}>
                        <option value="">Todas las regiones</option>
                        {(catalogos?.regiones || []).map(r => (
                            <option key={r.id ?? r} value={r.id ?? r}>{etiqueta(r)}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[220px]">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            placeholder="Buscar por persona o establecimiento"
                            className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:border-blue-500"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" checked={filtros.solo_fallas} onChange={e => set('solo_fallas', e.target.checked)} className="rounded" />
                        Solo con falla
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" checked={filtros.critico} onChange={e => set('critico', e.target.checked)} className="rounded" />
                        Solo críticos
                    </label>
                    <button
                        onClick={exportar}
                        disabled={exportando || reportes.length === 0}
                        className="ml-auto flex items-center gap-2 px-4 py-2.5 bg-sura-blue text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        {exportando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        Exportar Excel
                    </button>
                </div>
            </div>

            {/* Lista */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 size={32} className="animate-spin text-gray-400" />
                </div>
            ) : error ? (
                <div className="text-center py-16">
                    <p className="text-gray-500">{error}</p>
                    <button onClick={cargar} className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-sura-blue hover:underline">
                        <RefreshCw size={14} /> Reintentar
                    </button>
                </div>
            ) : reportes.length === 0 ? (
                <div className="text-center py-20">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ClipboardList size={32} className="text-gray-300" />
                    </div>
                    <p className="text-gray-500 text-lg">No hay reportes con estos filtros</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p className="text-xs text-gray-400 px-1">{reportes.length} {reportes.length === 1 ? 'resultado' : 'resultados'}</p>
                    {paginar(reportes, pagina).map(r => {
                        const fotos = (r.fotos || []).map(urlFoto).filter(Boolean);
                        const linea = etiqueta(r.linea_label || r.linea);
                        const region = etiqueta(r.region_label || r.region);
                        return (
                            <div key={r.id} className={`group bg-white rounded-2xl border-[1.5px] overflow-hidden transition-all hover:shadow-[0_4px_16px_rgba(0,0,0,0.04)] ${r.critico ? 'border-red-200' : 'border-slate-100 hover:border-slate-200'}`}>
                                <button
                                    type="button"
                                    onClick={() => setDetalleId(r.id)}
                                    className="w-full text-left px-6 py-5"
                                >
                                    <div className="flex items-start gap-[1.125rem]">
                                        <span className={`w-[50px] h-[50px] rounded-[14px] flex items-center justify-center shrink-0 ${r.critico ? 'bg-red-50 text-red-600' : r.hay_falla ? 'bg-blue-50 text-sura-blue' : 'bg-slate-100 text-slate-400'}`}>
                                            {React.createElement(r.hay_falla ? (ICONO_LINEA[r.linea] || ClipboardList) : ClipboardCheck, { size: 22 })}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-[13px] font-bold text-slate-400 tabular-nums">#{r.id}</span>
                                                {r.hay_falla && <BadgeEstado estado={r.estado} label={r.estado_label} />}
                                                <p className="font-semibold text-slate-900 text-[15px]">{nombrePersona(r)}</p>
                                                {(r.persona?.departamento_real || r.departamento_real) && <span className="text-xs text-gray-400">{etiqueta(r.persona?.departamento_real || r.departamento_real)}</span>}
                                                {r.critico && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700">
                                                        <ShieldAlert size={11} /> Crítico
                                                    </span>
                                                )}
                                                {!r.hay_falla && (
                                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500">Sin falla</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                {fechaHora(r.created_at)}
                                                {r.tomado_por && ` · ${r.estado === 'cerrado' ? 'Cerrado' : 'Atendido'} por ${etiqueta(r.estado === 'cerrado' ? (r.cerrado_por || r.tomado_por) : r.tomado_por)}`}
                                            </p>
                                            {r.hay_falla && (
                                                <>
                                                    <QueSeReporto className="mt-2 text-xs" linea={linea} desviaciones={r.desviaciones_detalle} />
                                                    {(r.lote || r.fecha_fabricacion) && (
                                                        <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-2">
                                                            <Tag size={12} className="shrink-0" />
                                                            {r.lote ? `Lote ${r.lote}` : `Sin lote · fabricado el ${new Date(r.fecha_fabricacion + 'T00:00:00').toLocaleDateString('es-VE')}`}
                                                        </p>
                                                    )}
                                                    {(region || r.establecimiento) && (
                                                        <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-2">
                                                            <MapPin size={12} className="shrink-0" />
                                                            <span className="truncate">{[region, r.establecimiento].filter(Boolean).join(' · ')}</span>
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            {r.hay_falla && (r.comentarios_count || 0) > 0 && (
                                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                                    <MessageSquare size={13} /> {r.comentarios_count} {r.comentarios_count === 1 ? 'mensaje' : 'mensajes'}
                                                </span>
                                            )}
                                            {fotos.length > 0 && (
                                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                                    <ImageIcon size={13} /> {fotos.length}
                                                </span>
                                            )}
                                            <ChevronRight size={20} className="text-slate-300 transition-transform group-hover:translate-x-[3px] group-hover:text-slate-400" />
                                        </div>
                                    </div>
                                </button>

                            </div>
                        );
                    })}
                    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden empty:hidden">
                        <Paginacion pagina={pagina} total={reportes.length} onCambiar={(p) => { setPagina(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                    </div>
                </div>
            )}

            {detalleId && (
                <DetalleReporte
                    diagnosticoId={detalleId}
                    onClose={() => setDetalleId(null)}
                    onCambio={() => cargar(true)}
                />
            )}
        </div>
    );
};

export default CalidadReportes;
